import type { EntryView, LiffSessionView } from '@koto-event/shared';
import { getAccessToken } from './liff.js';

/**
 * API クライアント。
 * LINE ID トークンを Authorization ヘッダに載せて送る。
 */

const BASE = import.meta.env.VITE_API_BASE ?? '';
/** 通信が終わらないまま操作や押印確認が止まることを防ぐ。更新要求は自動再送しない */
const REQUEST_TIMEOUT_MS = 30000;

let idToken: string | null = null;

export function setIdToken(token: string | null): void {
  idToken = token;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        // 本文のない POST（友だち確認）に Content-Type を付けると、空の本文として拒否されるため付けない
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...init?.headers,
      },
    });

    if (!res.ok) {
      let code = 'UNKNOWN';
      let message = '通信に失敗しました';
      try {
        const body = (await res.json()) as { error?: { code?: string; message?: string } };
        code = body.error?.code ?? code;
        message = body.error?.message ?? message;
      } catch {
        /* JSON でない場合は既定のメッセージを使う */
      }
      throw new ApiError(res.status, code, message);
    }

    return (await res.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, controller.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
      controller.signal.aborted
        ? '通信結果を確認できませんでした。通信状況を確認して、もう一度お試しください。'
        : '通信できませんでした。通信状況を確認して、もう一度お試しください。');
  } finally {
    clearTimeout(timer);
  }
}

/** API のパスを、押印画面（iframe）へ渡せる形にする。API が別オリジンの場合はそこへ向ける */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

export interface LiffConfig {
  liffId: string;
  addFriendUrl: string;
  rewardViewUrl: string;
  /** 電子スタンプが使える環境か（false なら押印枠を出さない） */
  digishotEnabled: boolean;
}

/** 押印の準備情報（サーバーが発行する。URL は改変せずそのまま押印画面へ渡す） */
export interface StampChallenge {
  sdkUrl: string;
  serviceUri: string;
  jqueryUrl: string;
  konvaUrl: string;
  userData: { entryId: string };
  expiresAt: string;
  serverNow: string;
}

/** 本人の友だち照会が必要な操作だけに送る。URL・SDK userData・永続ストレージには置かない */
function friendshipHeaders(): HeadersInit {
  const token = getAccessToken();
  return token ? { 'X-LIFF-Access-Token': token } : {};
}

export const api = {
  config: async (): Promise<LiffConfig> => {
    const config = await request<LiffConfig>('/api/liff/config');
    // 不正な成功応答でも「LIFF ID がないローカル環境」と取り違えない。
    if (!config || typeof config.liffId !== 'string' || typeof config.addFriendUrl !== 'string'
      || typeof config.rewardViewUrl !== 'string' || typeof config.digishotEnabled !== 'boolean') {
      throw new ApiError(0, 'INVALID_CONFIG', 'イベントの設定を読み込めませんでした');
    }
    return config;
  },

  startSession: (touchId: string) =>
    request<LiffSessionView>('/api/liff/session', {
      method: 'POST',
      headers: friendshipHeaders(),
      body: JSON.stringify({ touchId }),
    }),

  draw: (touchId: string) =>
    request<EntryView>('/api/liff/draw', {
      method: 'POST',
      headers: friendshipHeaders(),
      body: JSON.stringify({ touchId }),
    }),

  getEntry: (entryId: string) => request<EntryView>(`/api/liff/entries/${entryId}`),

  checkFriend: (entryId: string) =>
    request<EntryView>(`/api/liff/entries/${entryId}/friend-check`, {
      method: 'POST', headers: friendshipHeaders(),
    }),

  /** 押印の準備。SDK の読み込み先と送信先をサーバーから受け取る */
  stampChallenge: (entryId: string) =>
    request<StampChallenge>('/api/liff/digishot/challenge', {
      method: 'POST',
      headers: friendshipHeaders(),
      body: JSON.stringify({ entryId }),
    }),
};
